<script setup>
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import * as XLSX from 'xlsx';
import ReportService from '@/services/ReportService';
import {formatAmount, formatDate, formatNumber} from '@/helper';

/**
 * Záložka Dluhopisy v reportingu – dlaždice, dluhopisy ve struktuře a proklik na klienty.
 * Struktura (který poradce vidí které klienty) se řeší na BE stejně jako ve zbytku reportingu.
 * Objemy chodí po měnách, na CZK se nepřevádí. Export XLSX přes SheetJS jako stávající exportReport().
 */
const {t} = useI18n();
const loading = ref(true);
const summary = ref(null);
const bonds = ref([]);
/** Vybraný dluhopis – když je nastavený, místo seznamu dluhopisů se ukazují jeho klienti. */
const selectedBond = ref(null);
const clients = ref([]);
const loadingClients = ref(false);

onMounted(() => {
  Promise.all([ReportService.getBondsSummary(), ReportService.getBonds()]).then(([summaryData, bondsData]) => {
    summary.value = summaryData;
    bonds.value = bondsData;
  }).finally(() => {
    loading.value = false;
  });
});

/** Objem po měnách jako jeden řetězec („1 000 000 Kč · 20 000 €“); bez držeb 0 Kč. */
function formatVolumes(volumes) {
  if (!volumes?.length) {
    return formatAmount(0, 'CZK', 0);
  }
  return volumes.map(volume => formatAmount(volume.amount, volume.currency, 0)).join(' · ');
}

function showClients(bond) {
  selectedBond.value = bond;
  clients.value = [];
  loadingClients.value = true;
  ReportService.getBondClients(bond.id).then(response => {
    clients.value = response;
  }).finally(() => {
    loadingClients.value = false;
  });
}

const tiles = computed(() => summary.value ? [
  {label: t('reportsBonds.nominal_volume'), value: formatVolumes(summary.value.nominal_volume)},
  {label: t('reportsBonds.clients_with_bonds'), value: formatNumber(summary.value.clients_count, 0)},
  {label: t('reportsBonds.purchases_last_month'), value: formatNumber(summary.value.purchases_last_month, 0)},
  {
    label: t('reportsBonds.maturing_soon'),
    value: formatVolumes(summary.value.maturing_soon_volume),
    subvalue: t('reportsBonds.bonds_count', {count: summary.value.maturing_soon_bonds_count}),
  },
] : []);

/** Zapíše tabulku do XLSX; čísla zůstávají čísly, aby se v Excelu dala sčítat. */
function writeXlsx(rows, sheetName, fileName) {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), sheetName.substring(0, 31));
  XLSX.writeFile(workbook, fileName);
}

const xlsxDate = value => value ? formatDate(value, 'd.m.Y') : '';

/**
 * Tabulky jako ve zbytku reportingu (vue-good-table s řazením a stránkováním). Data chodí celá najednou,
 * proto lokální režim; data jsou ISO řetězce, takže se řadí správně i jako text.
 */
const paginationOptions = computed(() => ({
  enabled: true,
  perPage: 10,
  perPageDropdown: [10, 20, 50],
  rowsPerPageLabel: t('reportsIndex.entriesPerPage'),
  nextLabel: t('reportsIndex.next'),
  prevLabel: t('reportsIndex.previous'),
  ofLabel: 'z',
}));

const bondColumns = computed(() => [
  {label: t('reportsBonds.bond'), field: 'name'},
  {label: t('reportsBonds.issuer'), field: 'issuer'},
  {label: t('reportsBonds.maturity'), field: 'maturity_date'},
  {label: t('reportsBonds.clients_count'), field: 'clients_count', type: 'number'},
  {label: t('reportsBonds.quantity'), field: 'quantity', type: 'number'},
  {label: t('reportsBonds.nominal'), field: 'nominal_volume', type: 'number'},
]);

const clientColumns = computed(() => [
  {label: t('reportsBonds.client'), field: 'client_name'},
  {label: t('reportsBonds.quantity'), field: 'quantity', type: 'number'},
  {label: t('reportsBonds.nominal'), field: 'nominal_volume', type: 'number'},
  {label: t('reportsBonds.first_purchase'), field: 'first_purchase_date'},
  {label: t('reportsBonds.maturity'), field: 'maturity_date'},
]);

function exportBonds() {
  writeXlsx([
    [t('reportsBonds.bond'), 'ISIN', t('reportsBonds.issuer'), t('reportsBonds.maturity'), t('reportsBonds.clients_count'), t('reportsBonds.quantity'), t('reportsBonds.nominal'), t('reportsBonds.currency')],
    ...bonds.value.map(bond => [bond.name, bond.isin ?? '', bond.issuer ?? '', xlsxDate(bond.maturity_date), bond.clients_count, bond.quantity, bond.nominal_volume, bond.currency ?? '']),
  ], t('reportsBonds.bonds_sheet'), t('reportsBonds.bonds_file_name'));
}

function exportClients() {
  const bond = selectedBond.value;
  writeXlsx([
    [t('reportsBonds.client'), t('reportsBonds.advisor'), t('reportsBonds.quantity'), t('reportsBonds.nominal'), t('reportsBonds.currency'), t('reportsBonds.first_purchase'), t('reportsBonds.maturity')],
    ...clients.value.map(client => [client.client_name, client.intermediary_person_name ?? '', client.quantity, client.nominal_volume, client.currency, xlsxDate(client.first_purchase_date), xlsxDate(client.maturity_date)]),
  ], bond.isin ?? bond.name, t('reportsBonds.clients_file_name', {isin: bond.isin ?? bond.id}));
}
</script>

<template>
  <div class="page-section">
    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('reportsIndex.loading') }}</span></div>
    </div>
    <template v-else>
      <div class="row g-3 mb-4">
        <div v-for="tile of tiles" :key="tile.label" class="col-12 col-md-6 col-lg-3">
          <div class="card card--flat card--big-value h-100">
            <div class="card-body py-0 d-flex flex-column justify-content-center">
              <div class="card__value__label">{{ tile.label }}</div>
              <div class="card__value">{{ tile.value }}</div>
              <div v-if="tile.subvalue" class="card__subvalue">{{ tile.subvalue }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- seznam dluhopisů -->
      <div v-if="!selectedBond" class="card">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col"><div class="card-header__title">{{ t('reportsBonds.bonds_title') }}</div></div>
            <div class="col-auto">
              <button class="btn btn-primary" :disabled="!bonds.length" @click="exportBonds()">{{ t('reportsBonds.export') }}</button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <!-- klik na řádek otevře klienty dluhopisu -->
          <vue-good-table :columns="bondColumns" :rows="bonds" :sort-options="{enabled: true}" :pagination-options="paginationOptions"
                          :row-style-class="() => 'cursor-pointer'" @row-click="params => showClients(params.row)">
            <template #table-row="props">
              <template v-if="props.column.field === 'name'">
                <a href="#" @click.prevent>{{ props.row.name }}</a>
                <div class="table__sub-value">{{ props.row.isin ?? '---' }}</div>
              </template>
              <span v-else-if="props.column.field === 'issuer'">{{ props.row.issuer ?? '---' }}</span>
              <template v-else-if="props.column.field === 'maturity_date'">
                {{ props.row.maturity_date ? formatDate(props.row.maturity_date, 'd.m.Y') : '---' }}
                <!-- splatnost do 12 měsíců – poradce má klienty včas oslovit s reinvesticí -->
                <span v-if="props.row.maturing_soon" class="badge bg-warning text-dark ms-1">{{ t('reportsBonds.maturing_soon_badge') }}</span>
              </template>
              <span v-else-if="props.column.field === 'clients_count'">{{ formatNumber(props.row.clients_count, 0) }}</span>
              <span v-else-if="props.column.field === 'quantity'">{{ formatNumber(props.row.quantity, 0) }} {{ t('reportsBonds.pieces') }}</span>
              <span v-else-if="props.column.field === 'nominal_volume'">{{ formatAmount(props.row.nominal_volume, props.row.currency, 0) }}</span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
            <template v-slot:emptystate>
              <div class="text-center">{{ t('reportsBonds.no_bonds') }}</div>
            </template>
          </vue-good-table>
        </div>
      </div>

      <!-- klienti vybraného dluhopisu -->
      <div v-else class="card">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col">
              <a href="#" class="page-section__heading__back" @click.prevent="selectedBond = null"><i class="mdi mdi-arrow-left"></i> {{ t('reportsBonds.back_to_bonds') }}</a>
              <div class="card-header__title mt-2">{{ selectedBond.name }}</div>
              <div class="table__sub-value">{{ selectedBond.isin ?? '---' }} · {{ t('reportsBonds.maturity') }} {{ selectedBond.maturity_date ? formatDate(selectedBond.maturity_date, 'd.m.Y') : '---' }}</div>
            </div>
            <div class="col-auto">
              <button class="btn btn-primary" :disabled="loadingClients || !clients.length" @click="exportClients()">{{ t('reportsBonds.export') }}</button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loadingClients" class="text-center">
            <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('reportsIndex.loading') }}</span></div>
          </div>
          <vue-good-table v-else :columns="clientColumns" :rows="clients" :sort-options="{enabled: true}" :pagination-options="paginationOptions">
            <template #table-row="props">
              <template v-if="props.column.field === 'client_name'">
                <!-- na detailu klienta je karta Dluhopisy -->
                <router-link :to="{name: 'ClientDetail', params: {id: props.row.client_id}}">{{ props.row.client_name }}</router-link>
                <div class="table__sub-value">{{ props.row.intermediary_person_name ?? '---' }}</div>
              </template>
              <span v-else-if="props.column.field === 'quantity'">{{ formatNumber(props.row.quantity, 0) }} {{ t('reportsBonds.pieces') }}</span>
              <span v-else-if="props.column.field === 'nominal_volume'">{{ formatAmount(props.row.nominal_volume, props.row.currency, 0) }}</span>
              <span v-else-if="props.column.field === 'first_purchase_date' || props.column.field === 'maturity_date'">{{ props.row[props.column.field] ? formatDate(props.row[props.column.field], 'd.m.Y') : '---' }}</span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
            <template v-slot:emptystate>
              <div class="text-center">{{ t('reportsBonds.no_clients') }}</div>
            </template>
          </vue-good-table>
        </div>
      </div>
    </template>
  </div>
</template>
